import {
    type FC,
    type ForwardRefExoticComponent,
    type PropsWithoutRef,
    type RefAttributes,
    useRef,
} from "react";

import { StyledMLCollapse } from "./Styled";

type AnyProps = Record<string, unknown>;
type EmptyProps = Record<never, never>;

export type HeaderComp<TItem extends { id: string }> =
    ForwardRefExoticComponent<
        PropsWithoutRef<{ item: TItem }> & RefAttributes<HTMLDivElement>
    >;

export type BodyComp<
    TItem extends { id: string },
    P extends AnyProps = EmptyProps,
> = FC<{ item: TItem } & P>;

export interface ItemListViewProps<
    TItem extends { id: string },
    P extends AnyProps = EmptyProps,
    H extends AnyProps = EmptyProps,
> {
    items: TItem[];
    activeCollapse?: string | null;
    onChangeCollapse: (key: string) => void;

    HeaderComponent: HeaderComp<TItem>;
    BodyComponent: BodyComp<TItem, P>;

    bodyProps?: P;
    headerProps?: H;
}

export const ItemListView = <
    TItem extends { id: string },
    P extends AnyProps = EmptyProps,
    H extends AnyProps = EmptyProps,
>({
    items,
    activeCollapse,
    onChangeCollapse,
    HeaderComponent,
    BodyComponent,
    bodyProps,
    headerProps,
}: ItemListViewProps<TItem, P, H>) => {
    const itemRefs = useRef<Record<string, HTMLDivElement | null>>({});

    const onChange = (key: string | string[]) => {
        const newActiveKey = Array.isArray(key) ? key[0] : key;

        if (!newActiveKey && activeCollapse) {
            setTimeout(() => {
                const el = itemRefs.current[activeCollapse];
                el?.scrollIntoView({ behavior: "smooth", block: "start" });
            }, 300);
        }

        onChangeCollapse(newActiveKey);
    };

    return (
        <StyledMLCollapse
            activeKey={activeCollapse ?? undefined}
            expandIconPosition="end"
            destroyOnHidden={false}
            onChange={onChange}
            items={items.map((item) => {
                const key = item.id;
                const hidden = !!activeCollapse && key !== activeCollapse;

                return {
                    key,
                    style: hidden
                        ? { display: "none", pointerEvents: "none" }
                        : {},
                    label: (
                        <HeaderComponent
                            item={item}
                            ref={(el: HTMLDivElement | null) => {
                                if (el) itemRefs.current[key] = el;
                            }}
                            {...(headerProps as H)}
                        />
                    ),
                    children: (
                        <BodyComponent item={item} {...(bodyProps as P)} />
                    ),
                };
            })}
        />
    );
};
